import {Injector, Injectable, computed, inject, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, Subscription, map, of} from 'rxjs';
import {toObservable} from '@angular/core/rxjs-interop';
import {catchError, distinctUntilChanged, take, tap} from 'rxjs/operators';
import * as signalR from '@microsoft/signalr';
import {AppConfigService} from './app-config.service';
import {AuthService} from './auth.service';
import {StorageService} from './storage.service';
import {
  ChatMessage,
  ChatPreviewItem,
  ChatThread,
  ChatTypingState,
  ChatUser
} from '../Components/common/chat/chat-thread/chat-thread.model';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private readonly injector = inject(Injector);
  private readonly apiBaseUrl: string;
  private readonly threadsState = signal<ChatThread[]>([]);
  private readonly messagesState = signal<Record<string, ChatMessage[]>>({});
  private readonly typingState = signal<Record<string, ChatTypingState[]>>({});
  private hubConnection: signalR.HubConnection | null = null;
  private activeThreadId = '';
  private subscribedThreadId = '';
  private realtimeUserId = '';
  private realtimeAccessToken = '';
  private authTokenSubscription: Subscription | null = null;
  private realtimeUnloadGuardInitialized = false;
  private realtimePageUnloading = false;
  private isUserLoggingOut = false;
  private realtimeEnabled = false;
  private realtimeDisconnectInProgress = false;
  private initialRealtimeReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private realtimeHandlersConnection: signalR.HubConnection | null = null;
  private realtimePreferredTransport: signalR.HttpTransportType = signalR.HttpTransportType.WebSockets;
  private threadsLoadInFlight = false;
  private threadsLoadedUserId = '';
  private readonly joinThreadMethodCandidates = [
    'JoinThreadGroup',
    'JoinThread',
    'SubscribeToThread',
    'AddToThreadGroup',
    'JoinThreadRoom'
  ];
  private readonly leaveThreadMethodCandidates = [
    'LeaveThreadGroup',
    'LeaveThread',
    'UnsubscribeFromThread',
    'RemoveFromThreadGroup',
    'UnjoinThreadGroup'
  ];
  private readonly realtimeConnectionState = signal<signalR.HubConnectionState>(signalR.HubConnectionState.Disconnected);
  private readonly realtimeRetryPolicy: signalR.IRetryPolicy = {
    nextRetryDelayInMilliseconds: (retryContext) => {
      const shouldRetry = this.shouldMaintainRealtimeConnection();
      const retryDelays = [2500, 2500, 5000, 10000, 20000, 60000];
      const previousRetryCount = retryContext?.previousRetryCount ?? 0;
      const nextDelay = shouldRetry
        ? retryDelays[Math.min(previousRetryCount, retryDelays.length - 1)]
        : null;

      this.logDebug('[Chat][SignalR][reconnect] Automatic reconnect retry decision', this.getRealtimeLogState({
        previousRetryCount,
        elapsedMilliseconds: retryContext?.elapsedMilliseconds ?? 0,
        retryReason: retryContext?.retryReason ? String(retryContext.retryReason) : '(none)',
        shouldRetry,
        nextDelayMs: nextDelay
      }));

      return nextDelay;
    }
  };
  private readonly topbarPreviewItemsState = computed<ChatPreviewItem[]>(() =>
    [...this.threadsState()]
      .sort((a, b) => {
        const aTime = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
        const bTime = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
        return bTime - aTime;
      })
      .map((thread) => ({
        threadId: thread.id,
        senderName: thread.lastMessage?.senderName || thread.participants?.[0]?.name || thread.title,
        snippet: thread.lastMessage?.body || 'No messages yet',
        timestamp: thread.lastMessage?.createdAt || '',
        unreadCount: thread.unreadCount,
        avatarUrl: thread.lastMessage?.senderAvatarUrl || thread.participants?.[0]?.avatarUrl
      }))
  );
  private readonly unreadCountState = signal<number>(0);
  private readonly chatDebugLoggingEnabled = false;

  readonly threads = this.threadsState.asReadonly();
  readonly messages = this.messagesState.asReadonly();
  readonly typing = this.typingState.asReadonly();
  readonly realtimeConnectionStateReadonly = this.realtimeConnectionState.asReadonly();

  readonly threads$ = toObservable(this.threadsState, {injector: this.injector});
  readonly messages$ = toObservable(this.messagesState, {injector: this.injector});
  readonly typing$ = toObservable(this.typingState, {injector: this.injector});

  private logDebug(...args: unknown[]): void {
    if (!this.chatDebugLoggingEnabled) {
      return;
    }

    console.log(...args);
  }

  private logWarn(...args: unknown[]): void {
    if (!this.chatDebugLoggingEnabled) {
      return;
    }

    console.warn(...args);
  }

  private logError(...args: unknown[]): void {
    if (!this.chatDebugLoggingEnabled) {
      return;
    }

    console.error(...args);
  }

  private isRealtimeTeardownActive(): boolean {
    return this.isUserLoggingOut || this.realtimePageUnloading || this.realtimeDisconnectInProgress;
  }

  private clearInitialReconnectTimer(): void {
    if (!this.initialRealtimeReconnectTimer) {
      return;
    }

    clearTimeout(this.initialRealtimeReconnectTimer);
    this.initialRealtimeReconnectTimer = null;
  }

  private async stopRealtimeConnection(connection: signalR.HubConnection, context: string): Promise<void> {
    try {
      await connection.stop();
      this.logDebug(`[Chat][SignalR] ${context} - connection stopped`, {connectionId: connection.connectionId});
    } catch (error) {
      this.logError(`[Chat][SignalR] ${context} - error while stopping connection: ${String(error)}`, {
        connectionId: connection.connectionId
      });
    }
  }

  isRealtimeConnected(): boolean {
    return this.realtimeConnectionState() === signalR.HubConnectionState.Connected;
  }

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private storageService: StorageService
  ) {
    this.apiBaseUrl = this.appConfigService.apiBaseUrl;
    this.ensureRealtimeUnloadGuard();
  }

  private getChat<T>(path: string): Observable<T> {
    return this.http.get<T>(`${this.apiBaseUrl}${path}`).pipe(take(1));
  }

  private postChat<T>(path: string, body: any): Observable<T> {
    return this.http.post<T>(`${this.apiBaseUrl}${path}`, body).pipe(take(1));
  }

  private deleteChat<T>(path: string): Observable<T> {
    return this.http.delete<T>(`${this.apiBaseUrl}${path}`).pipe(take(1));
  }

  private get chatHubUrl(): string {
    const baseOrigin = new URL(this.apiBaseUrl, window.location.origin).origin;
    return `${baseOrigin}/hubs/chat`;
  }

  private getRealtimeAccessToken(): string {
    try {
      const currentUserRaw = localStorage.getItem('currentUser');
      if (!currentUserRaw) {
        return '';
      }

      const currentUser = JSON.parse(currentUserRaw);
      return (currentUser?.accessToken || '').trim();
    } catch {
      return '';
    }
  }

  async connectRealtime(userId: string): Promise<void> {
    this.ensureRealtimeTokenSync();

    const normalizedUserId = (userId || '').trim();
    const normalizedToken = (this.realtimeAccessToken || '').trim();

    if (!normalizedUserId) {
      this.logWarn('[Chat][SignalR] connectRealtime skipped - missing userId/token', this.getRealtimeLogState({
        receivedUserId: userId || '(empty)',
        hasToken: !!normalizedToken
      }));
      return;
    }

    this.realtimeUserId = normalizedUserId;
    this.realtimeEnabled = true;

    if (!normalizedToken) {
      this.logDebug('[Chat][SignalR] connectRealtime waiting for token before starting hub', this.getRealtimeLogState({
        receivedUserId: userId || '(empty)',
        hasToken: false
      }));
      return;
    }

    if (this.isRealtimeTeardownActive()) {
      this.logWarn('[Chat][SignalR] connectRealtime skipped - teardown in progress', this.getRealtimeLogState({
        realtimePageUnloading: this.realtimePageUnloading,
        isUserLoggingOut: this.isUserLoggingOut,
        realtimeDisconnectInProgress: this.realtimeDisconnectInProgress
      }));
      return;
    }

    this.realtimeAccessToken = normalizedToken;

    this.logDebug('[Chat][SignalR] connectRealtime requested', this.getRealtimeLogState({
      hasToken: !!this.realtimeAccessToken
    }));

    if (!this.shouldMaintainRealtimeConnection()) {
      this.logWarn('[Chat][SignalR] connectRealtime skipped - guard failed', this.getRealtimeLogState({
        realtimeEnabled: this.realtimeEnabled,
        isUserLoggingOut: this.isUserLoggingOut,
        realtimePageUnloading: this.realtimePageUnloading,
        realtimeDisconnectInProgress: this.realtimeDisconnectInProgress,
        hasRealtimeUserId: !!this.realtimeUserId,
        hasRealtimeAccessToken: !!this.realtimeAccessToken
      }));

      if (this.realtimeDisconnectInProgress) {
        return;
      }

      await this.disconnectRealtimeInternal();
      return;
    }

    if (!this.hubConnection) {
      const selectedTransport = this.realtimePreferredTransport;

      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(this.chatHubUrl, {
          withCredentials: true,
          accessTokenFactory: () => this.realtimeAccessToken,
          transport: selectedTransport
        })
        .configureLogging(signalR.LogLevel.None)
        .withAutomaticReconnect(this.realtimeRetryPolicy)
        .build();

      this.logDebug('[Chat][SignalR] Built hub connection', this.getRealtimeLogState({
        transport: signalR.HttpTransportType[selectedTransport]
      }));
    }

    this.registerRealtimeHandlers();

    const currentConnectionState = this.realtimeConnectionState();
    if (
      currentConnectionState === signalR.HubConnectionState.Connected ||
      currentConnectionState === signalR.HubConnectionState.Connecting ||
      currentConnectionState === signalR.HubConnectionState.Reconnecting
    ) {
      this.logDebug('[Chat][SignalR] connectRealtime skipped - already connecting/connected', this.getRealtimeLogState());
      return;
    }

    const connection = this.hubConnection;
    this.realtimeConnectionState.set(signalR.HubConnectionState.Connecting);
    this.logDebug('[Chat][SignalR] Starting hub connection', this.getRealtimeLogState({
      hubUrl: this.chatHubUrl
    }));

    try {
      await connection.start();
      this.logDebug('[Chat][SignalR] Connection started');

      if (this.hubConnection !== connection || !this.shouldMaintainRealtimeConnection()) {
        this.logDebug('[Chat][SignalR] Connection started but no longer needed, stopping', this.getRealtimeLogState());
        await this.stopRealtimeConnection(connection, 'connectRealtime post-start guard');
        return;
      }

      this.clearInitialReconnectTimer();

      this.realtimeConnectionState.set(signalR.HubConnectionState.Connected);
      this.trySyncActiveThreadRealtimeSubscription();
      this.logDebug('[Chat][SignalR] Connected', {
        userId: this.realtimeUserId,
        hubUrl: this.chatHubUrl,
        connectionId: connection.connectionId
      });
    } catch (error: unknown) {
      this.logError(`[Chat][SignalR] Error while starting connection: ${String(error)}`);
      this.realtimeConnectionState.set(signalR.HubConnectionState.Disconnected);

      if (this.realtimePreferredTransport === signalR.HttpTransportType.WebSockets) {
        this.logWarn('[Chat][SignalR] WebSocket connect failed, falling back to LongPolling', this.getRealtimeLogState({
          error: String(error)
        }));
        this.realtimePreferredTransport = signalR.HttpTransportType.LongPolling;
        this.hubConnection = null;

        if (this.realtimeUserId && this.shouldMaintainRealtimeConnection()) {
          await this.connectRealtime(this.realtimeUserId);
          return;
        }
      }

      this.logError('[Chat][SignalR] Initial connection failed', {
        userId: this.realtimeUserId,
        hubUrl: this.chatHubUrl,
        error
      });

      if (
        this.hubConnection === connection
        && this.shouldMaintainRealtimeConnection()
        && !this.initialRealtimeReconnectTimer
      ) {
        this.initialRealtimeReconnectTimer = setTimeout(() => {
          this.initialRealtimeReconnectTimer = null;

          if (!this.shouldMaintainRealtimeConnection() || !this.realtimeUserId) {
            return;
          }

          void this.connectRealtime(this.realtimeUserId);
        }, 2000);
      }
    }
  }

  setActiveThread(threadId: string): void {
    const nextThreadId = (threadId || '').trim();

    this.logDebug('[Chat][SignalR] setActiveThread requested', this.getRealtimeLogState({
      nextThreadId,
      currentThreadId: this.activeThreadId
    }));

    if (this.activeThreadId === nextThreadId) {
      return;
    }

    this.activeThreadId = nextThreadId;
    this.trySyncActiveThreadRealtimeSubscription();
  }

  async disconnectRealtime(): Promise<void> {
    this.logDebug('[Chat][SignalR] disconnectRealtime requested', this.getRealtimeLogState());
    this.realtimeEnabled = false;
    this.isUserLoggingOut = false;
    await this.disconnectRealtimeInternal();
  }

  private async disconnectRealtimeInternal(options?: { preserveIntent?: boolean; markUserLoggingOut?: boolean }): Promise<void> {
    this.logDebug('[Chat][SignalR] disconnectRealtimeInternal started', this.getRealtimeLogState(options || {}));

    this.realtimeDisconnectInProgress = true;
    this.isUserLoggingOut = !!options?.markUserLoggingOut;
    this.activeThreadId = '';
    this.subscribedThreadId = '';

    // Immediately set realtimeEnabled to false to prevent any re-connection
    // or further hub invocations during the teardown process.
    if (!options?.preserveIntent) {
      this.realtimeEnabled = false;
    }

    this.clearInitialReconnectTimer();

    const connection = this.hubConnection;
    this.hubConnection = null;
    this.realtimeHandlersConnection = null;

    if (connection) {
      this.realtimeConnectionState.set(signalR.HubConnectionState.Disconnecting);
    } else {
      this.realtimeConnectionState.set(signalR.HubConnectionState.Disconnected);
    }

    try {
      if (connection) {
        this.logDebug('[Chat][SignalR] disconnectRealtimeInternal calling hubConnection.stop', this.getRealtimeLogState({
          connectionId: connection.connectionId,
          connectionState: signalR.HubConnectionState[connection.state]
        }));

        await this.stopRealtimeConnection(connection, 'disconnectRealtimeInternal');
      }
    } finally {
      this.realtimeDisconnectInProgress = false;
      this.realtimeConnectionState.set(signalR.HubConnectionState.Disconnected);
    }
  }

  private async stopStaleRealtimeConnection(connection: signalR.HubConnection): Promise<void> {
    this.logDebug('[Chat][SignalR] stopStaleRealtimeConnection requested', this.getRealtimeLogState({
      connectionState: signalR.HubConnectionState[connection.state],
      connectionId: connection.connectionId
    }));
    if (
      connection.state === signalR.HubConnectionState.Disconnected ||
      connection.state === signalR.HubConnectionState.Disconnecting
    ) {
      return;
    }

    await this.stopRealtimeConnection(connection, 'stopStaleRealtimeConnection');
  }

  private ensureRealtimeTokenSync(): void {
    if (this.authTokenSubscription) {
      return;
    }

    this.logDebug('[Chat][SignalR] Initializing authTokenSubscription', this.getRealtimeLogState());

    const authService = this.injector.get(AuthService);
    this.authTokenSubscription = authService.accessToken$.subscribe(async (nextToken) => {
      const normalizedToken = (nextToken || '').trim();

      this.logDebug('[Chat][SignalR] authTokenSubscription received new token', this.getRealtimeLogState({
        hasToken: !!normalizedToken,
        tokenLength: normalizedToken.length
      }));

      if (!normalizedToken) {
        await this.disconnectRealtimeInternal({markUserLoggingOut: true});
        this.realtimeAccessToken = '';
        this.realtimeUserId = '';
        return;
      }

      this.isUserLoggingOut = false;
      if (normalizedToken === this.realtimeAccessToken) {
        return;
      }

      this.realtimeAccessToken = normalizedToken;
      this.logDebug('[Chat][SignalR] Token updated', this.getRealtimeLogState({
        realtimeEnabled: this.realtimeEnabled,
        hasUserId: !!this.realtimeUserId
      }));
      if (this.realtimeEnabled) {
        if (!this.shouldMaintainRealtimeConnection()) {
          return;
        }

        const currentConnectionState = this.realtimeConnectionState();
        if (
          !this.hubConnection
          || currentConnectionState === signalR.HubConnectionState.Disconnected
        ) {
          this.logDebug('[Chat][SignalR] Token updated - attempting initial/recovery connect', this.getRealtimeLogState({
            hasToken: true
          }));
          void this.connectRealtime(this.realtimeUserId);
          return;
        }

        void this.reconnectRealtimeForTokenChange();
      }
    });
  }

  private ensureRealtimeUnloadGuard(): void {
    if (this.realtimeUnloadGuardInitialized) {
      return;
    }

    this.realtimeUnloadGuardInitialized = true;

    const markUnloading = async (): Promise<void> => {
      this.isUserLoggingOut = true;
      this.realtimePageUnloading = true;
      this.realtimeEnabled = false;
      await this.disconnectRealtimeInternal({markUserLoggingOut: true});
    };

    window.addEventListener('beforeunload', markUnloading);
    window.addEventListener('pagehide', markUnloading);
  }

  private async reconnectRealtimeForTokenChange(): Promise<void> {
    this.logDebug('[Chat][SignalR] reconnectRealtimeForTokenChange requested', this.getRealtimeLogState());
    if (
      !this.realtimeEnabled ||
      this.isRealtimeTeardownActive() ||
      !this.realtimeUserId ||
      !this.hubConnection ||
      this.realtimeConnectionState() !== signalR.HubConnectionState.Connected
    ) {
      return;
    }

    const userId = this.realtimeUserId;
    await this.disconnectRealtimeInternal({preserveIntent: true});
    void this.connectRealtime(userId);
  }

  private shouldMaintainRealtimeConnection(): boolean {
    const shouldMaintain = this.realtimeEnabled
      && !this.isRealtimeTeardownActive()
      && !!this.realtimeUserId
      && !!this.realtimeAccessToken;

    if (!shouldMaintain && this.realtimeEnabled) {
      this.logDebug('[Chat][SignalR] shouldMaintainRealtimeConnection decision', this.getRealtimeLogState({
        realtimeEnabled: this.realtimeEnabled,
        isUserLoggingOut: this.isUserLoggingOut,
        realtimePageUnloading: this.realtimePageUnloading,
        realtimeDisconnectInProgress: this.realtimeDisconnectInProgress,
        hasUserId: !!this.realtimeUserId,
        hasToken: !!this.realtimeAccessToken,
        shouldMaintain
      }));
    }

    return shouldMaintain;
  }

  private canSendChatHttpRequests(): boolean {
    return !!this.getRealtimeAccessToken();
  }

  private buildThreadGroupInvocationCandidates(threadId: string): unknown[][] {
    const groupName = `thread:${threadId}`;
    const candidates: unknown[][] = [];

    // Prioritize method overloads that include userId if available
    if (this.realtimeUserId) {
      candidates.push([threadId, this.realtimeUserId]);
      candidates.push([groupName, this.realtimeUserId]);
    }

    // Fallback to simple overloads
    candidates.push([threadId]);
    candidates.push([groupName]);

    return candidates;
  }

  private getRealtimeLogState(extra: Record<string, unknown> = {}): Record<string, unknown> {
    const stack = (new Error()).stack || '';
    const stackLines = stack.split('\n');
    const filteredStack = stackLines
      .filter(line => !line.includes('getRealtimeLogState') && !line.includes('Error'))
      .slice(0, 5)
      .map(line => line.trim());

    return {
      source: 'SignalR',
      connectionState: signalR.HubConnectionState[this.realtimeConnectionState()],
      activeThreadId: this.activeThreadId || '(none)',
      subscribedThreadId: this.subscribedThreadId || '(none)',
      realtimeUserId: this.realtimeUserId || '(none)',
      stack: filteredStack,
      ...extra
    };
  }

  private registerRealtimeHandlers(): void {
    if (!this.hubConnection) {
      return;
    }

    if (this.realtimeHandlersConnection === this.hubConnection) {
      return;
    }

    this.realtimeHandlersConnection = this.hubConnection;

    this.hubConnection.onreconnecting(() => {
      this.realtimeConnectionState.set(signalR.HubConnectionState.Reconnecting);

      this.logWarn('[Chat][SignalR][state] Reconnecting', this.getRealtimeLogState());
    });

    this.hubConnection.onreconnected(() => {
      this.realtimeConnectionState.set(signalR.HubConnectionState.Connected);
      this.subscribedThreadId = '';
      this.trySyncActiveThreadRealtimeSubscription();

      if (!this.activeThreadId || !this.realtimeUserId) {
        return;
      }

      this.logDebug(
        '[Chat][SignalR][state] Reconnected - refreshing active thread messages',
        this.getRealtimeLogState({
          threadId: this.activeThreadId
        })
      );
      this.loadMessages(this.activeThreadId, this.realtimeUserId);
    });

    this.hubConnection.onclose(() => {
      this.realtimeConnectionState.set(signalR.HubConnectionState.Disconnected);
      this.subscribedThreadId = '';

      this.logWarn('[Chat][SignalR][state] Connection closed', this.getRealtimeLogState());
    });

    this.hubConnection.on('ReceiveMessage', (payload: unknown) => {
      const rawPayload = payload as Record<string, unknown>;
      this.logDebug(
        '[Chat][SignalR][receive] ReceiveMessage raw payload',
        this.getRealtimeLogState({
          payloadType: typeof payload,
          payloadKeys: rawPayload && typeof rawPayload === 'object' ? Object.keys(rawPayload) : [],
          payload
        })
      );

      const message = this.normalizeMessage(payload);
      if (!message.id || !message.threadId) {
        this.logWarn(
          '[Chat][SignalR][receive] ReceiveMessage payload missing required ids',
          this.getRealtimeLogState({
            payload,
            normalizedMessage: message
          })
        );
        return;
      }

      this.logDebug(
        '[Chat][SignalR][receive] ReceiveMessage normalized payload',
        this.getRealtimeLogState({
          messageId: message.id,
          threadId: message.threadId,
          senderUserId: message.senderUserId,
          body: message.body,
          createdAt: message.createdAt,
          attachmentUrl: message.attachmentUrl
        })
      );
      this.appendMessage(message);

      if (!this.realtimeUserId) {
        return;
      }

      if (message.threadId !== this.activeThreadId) {
        this.logDebug(
          '[Chat][SignalR][state] ReceiveMessage - skipping API refresh for inactive thread',
          this.getRealtimeLogState({
            threadId: message.threadId
          })
        );
        return;
      }

      this.logDebug(
        '[Chat][SignalR][state] ReceiveMessage - refreshing active thread via API',
        this.getRealtimeLogState({
          threadId: message.threadId
        })
      );
      this.loadMessages(message.threadId, this.realtimeUserId);
    });

    this.hubConnection.on('TypingChanged', (payload: unknown) => {
      const state = payload as ChatTypingState;
      if (!state?.threadId || !state?.userId) {
        return;
      }

      this.updateTypingState(state);
    });
  }

  private trySyncActiveThreadRealtimeSubscription(): void {
    this.logDebug('[Chat][SignalR] trySyncActiveThreadRealtimeSubscription check', this.getRealtimeLogState());
    if (
      !this.hubConnection ||
      this.hubConnection.state !== signalR.HubConnectionState.Connected ||
      !this.shouldMaintainRealtimeConnection() ||
      this.isUserLoggingOut
    ) {
      return;
    }

    const nextThreadId = this.activeThreadId;
    const previousThreadId = this.subscribedThreadId;

    if (previousThreadId && previousThreadId !== nextThreadId) {
      void this.invokeThreadGroupMethod(
        this.leaveThreadMethodCandidates,
        previousThreadId,
        'leave'
      );
      this.subscribedThreadId = '';
    }

    if (!nextThreadId || nextThreadId === this.subscribedThreadId) {
      return;
    }

    void this.invokeThreadGroupMethod(
      this.joinThreadMethodCandidates,
      nextThreadId,
      'join'
    ).then((didSubscribe) => {
      if (!didSubscribe) {
        return;
      }

      if (this.activeThreadId !== nextThreadId) {
        return;
      }

      this.subscribedThreadId = nextThreadId;
    });
  }

  private async invokeThreadGroupMethod(
    methodCandidates: string[],
    threadId: string,
    action: 'join' | 'leave'
  ): Promise<boolean> {
    this.logDebug(`[Chat][SignalR] ${action} thread group requested`, this.getRealtimeLogState({
      threadId,
      methodCandidates
    }));
    if (
      !this.hubConnection ||
      this.hubConnection.state !== signalR.HubConnectionState.Connected ||
      !this.shouldMaintainRealtimeConnection() ||
      this.isUserLoggingOut
    ) {
      return false;
    }

    const groupName = `thread:${threadId}`;
    const invocationCandidates = this.buildThreadGroupInvocationCandidates(threadId);
    const attemptedInvocations: Array<{ methodName: string; args: unknown[] }> = [];

    for (const methodName of methodCandidates) {
      for (const args of invocationCandidates) {
        try {
          if (this.hubConnection.state !== signalR.HubConnectionState.Connected) {
            this.logWarn(`[Chat][SignalR] ${action} thread group skipped - connection lost during retry loop`);
            return false;
          }
          await this.hubConnection.invoke(methodName, ...args);
          this.logDebug(`[Chat][SignalR] Thread group ${action} success`, {
            threadId,
            groupName,
            methodName,
            args
          });
          return true;
        } catch (error) {
          attemptedInvocations.push({methodName, args});
        }
      }
    }

    this.logWarn(`[Chat][SignalR] Unable to ${action} thread group`, {
      threadId,
      groupName,
      triedMethods: methodCandidates,
      attemptedInvocations
    });
    return false;
  }

  resolveAttachmentUrl(attachmentUrl?: string | null): string {
    if (!attachmentUrl) {
      return '';
    }

    if (/^https?:\/\//i.test(attachmentUrl)) {
      return attachmentUrl;
    }

    const baseOrigin = new URL(this.apiBaseUrl, window.location.origin).origin;
    return `${baseOrigin}${attachmentUrl.startsWith('/') ? attachmentUrl : `/${attachmentUrl}`}`;
  }

  resolveAvatarUrl(avatarUrl?: string | null): string {
    if (!avatarUrl) {
      return '';
    }

    // The backend returns a placeholder path ("default-avatar.png") for users without a
    // custom avatar, but that file doesn't actually exist on the avatars storage server
    // (it 404s). This placeholder can come back from any endpoint that joins sender/
    // participant avatar info (thread list, message list, auto-responder/bot users, etc.),
    // not just the current user's own preferences - so filter it here centrally rather than
    // resolving it into a broken URL that renders as a missing/blank avatar image.
    if (/default-avatar\.(png|jpg|jpeg|gif|svg)$/i.test(avatarUrl)) {
      return '';
    }

    if (/^https?:\/\//i.test(avatarUrl)) {
      return avatarUrl;
    }

    const prefix = (this.appConfigService.avatarsStorageUrlPrefix || '').replace(/\/$/, '');
    const cleanPath = avatarUrl.replace(/^\/uploads/, '');
    const relativePath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
    return `${prefix}${relativePath}`;
  }

  downloadAttachment(attachmentUrl?: string | null): Observable<Blob> {
    const resolvedUrl = this.resolveAttachmentUrl(attachmentUrl);
    return this.http.get(resolvedUrl, {responseType: 'blob'}).pipe(take(1));
  }

  private unwrapData<T>(result: any): T {
    const data = result?.Data ?? result;

    if (Array.isArray(data)) {
      return (data[0] ?? null) as T;
    }

    return data as T;
  }

  loadThreads(userId: string): void {
    const normalizedUserId = (userId || '').trim();
    if (!normalizedUserId) {
      return;
    }

    if (!this.canSendChatHttpRequests()) {
      this.threadsState.set([]);
      this.threadsLoadInFlight = false;
      this.threadsLoadedUserId = '';
      return;
    }

    if (this.threadsLoadInFlight || this.threadsLoadedUserId === normalizedUserId) {
      return;
    }

    this.threadsLoadInFlight = true;
    this.getChat<any>(`chat/threads?userId=${encodeURIComponent(normalizedUserId)}`)
      .subscribe((result: any) => {
        const data = Array.isArray(result?.Data) ? result.Data : [];
        this.logDebug('[ChatThreads] API participants payload', data.map((thread: any) => ({
          threadId: this.normalizeThread(thread).id,
          apiThreadId: thread?.id ?? thread?.Id,
          title: thread?.title ?? thread?.Title,
          participants: thread?.participants ?? thread?.Participants ?? []
        })));
        this.threadsState.set(
          data
            .filter((thread: any) => !this.isThreadDeleted(thread))
            .map((thread: ChatThread) => this.normalizeThread(thread))
        );
        this.unreadCountState.set(
          this.threadsState().reduce((sum, thread) => sum + (thread.unreadCount || 0), 0)
        );
        this.threadsLoadedUserId = normalizedUserId;
        this.threadsLoadInFlight = false;
      }, () => {
        this.threadsLoadInFlight = false;
      });
  }

  loadThread(threadId: string, userId: string): void {
    const normalizedThreadId = (threadId || '').trim();
    if (!normalizedThreadId) {
      return;
    }

    if (!this.canSendChatHttpRequests()) {
      return;
    }

    this.getChat<any>(`chat/thread/${encodeURIComponent(normalizedThreadId)}?userId=${encodeURIComponent(userId)}`)
      .subscribe((result: any) => {
        const thread = this.unwrapData<any>(result);
        if (!thread || this.isThreadDeleted(thread)) {
          this.threadsState.set(this.threadsState().filter((item) => item.id !== normalizedThreadId));
          return;
        }

        this.upsertThread(this.normalizeThread(thread));
      });
  }

  loadMessages(threadId: string, userId: string): void {
    if (!threadId) {
      return;
    }

    if (!this.canSendChatHttpRequests()) {
      const current = {...this.messagesState()};
      current[threadId] = [];
      this.messagesState.set(current);
      return;
    }

    this.logDebug('[Chat][Fetch] Loading messages via API', {
      threadId,
      userId
    });

    this.getChat<any>(`chat/messages?threadId=${encodeURIComponent(threadId)}&userId=${encodeURIComponent(userId)}`)
      .subscribe((result: any) => {
        const data = Array.isArray(result?.Data) ? result.Data : [];
        this.logDebug('[Chat][Fetch] Messages loaded', {
          threadId,
          count: data.length
        });
        const current = {...this.messagesState()};
        current[threadId] = data.map((message: ChatMessage) => this.normalizeMessage(message));
        this.messagesState.set(current);
      });
  }

  loadUsers(userId: string): Observable<ChatUser[]> {
    if (!this.canSendChatHttpRequests()) {
      return of([]);
    }

    return this.getChat<any>(`chat/users?userId=${encodeURIComponent(userId)}`).pipe(
      map((result: any) => {
        const data = Array.isArray(result?.Data) ? result.Data : [];
        return data.map((u: any) => ({
          userId: (u?.userId ?? u?.UserId ?? '').toString(),
          name: (u?.userName ?? u?.UserName ?? u?.name ?? u?.Name ?? '').toString(),
          avatarUrl: this.resolveAvatarUrl(u?.avatarUrl ?? u?.AvatarUrl)
        })) as ChatUser[];
      })
    );
  }

  createOrGetThread(currentUserId: string, recipientUserId: string): Observable<ChatThread> {
    return this.createOrGetThreadForParticipants(currentUserId, [recipientUserId]);
  }

  createOrGetThreadForParticipants(currentUserId: string, participantUserIds: string[], title = ''): Observable<ChatThread> {
    const normalizedParticipantUserIds = [...new Set((participantUserIds || []).map((id) => (id || '').trim()).filter(Boolean))];
    const payload: Record<string, any> = {
      currentUserId,
      participantUserIds: normalizedParticipantUserIds,
      title
    };

    if (normalizedParticipantUserIds.length === 1) {
      payload.recipientUserId = normalizedParticipantUserIds[0];
    }

    return this.postChat<any>('chat/thread', payload).pipe(
      map((result: any) => {
        const thread = this.normalizeThread(this.unwrapData<ChatThread>(result));
        this.upsertThread(thread);
        return thread;
      })
    );
  }

  addParticipants(threadId: string, currentUserId: string, participantUserIds: string[]): Observable<ChatThread> {
    const normalizedParticipantUserIds = [...new Set((participantUserIds || []).map((id) => (id || '').trim()).filter(Boolean))];
    return this.postChat<any>('chat/thread/participants', {
      ThreadId: threadId,
      CurrentUserId: currentUserId,
      NewParticipantUserIds: normalizedParticipantUserIds
    }).pipe(
      map((result: any) => {
        const thread = this.normalizeThread(this.unwrapData<ChatThread>(result));
        this.upsertThread(thread);
        return thread;
      })
    );
  }

  removeParticipant(threadId: string, currentUserId: string, participantUserId: string): Observable<boolean> {
    return this.postChat<any>('chat/thread/remove-participant', {
      threadId,
      currentUserId,
      participantUserId
    }).pipe(
      map((result: any) => Boolean(this.unwrapData<boolean>(result)))
    );
  }

  sendMessage(threadId: string, senderUserId: string, body: string, attachment?: File | null): Observable<ChatMessage> {
    if (!attachment) {
      return this.postChat<any>('chat/messages', {
        threadId,
        senderUserId,
        body
      }).pipe(
        map((result: any) => this.normalizeMessage(this.unwrapData<ChatMessage>(result)))
      );
    }

    const form = new FormData();
    form.append('ThreadId', threadId);
    form.append('SenderUserId', senderUserId);
    form.append('Body', body);
    form.append('File', attachment, attachment.name);

    return this.postChat<any>('chat/messages/upload', form).pipe(
      map((result: any) => this.normalizeMessage(this.unwrapData<ChatMessage>(result)))
    );
  }

  deleteThread(threadId: string, userId: string): Observable<boolean> {
    return this.deleteChat<any>(`chat/thread/${encodeURIComponent(threadId)}?userId=${encodeURIComponent(userId)}`).pipe(
      map((result: any) => {
        const deleted = Boolean(this.unwrapData<boolean>(result));

        if (deleted) {
          const updatedThreads = this.threadsState().filter((thread) => thread.id !== threadId);
          this.threadsState.set(updatedThreads);

          const messagesByThread = {...this.messagesState()};
          delete messagesByThread[threadId];
          this.messagesState.set(messagesByThread);

          const typingByThread = {...this.typingState()};
          delete typingByThread[threadId];
          this.typingState.set(typingByThread);
        }

        return deleted;
      })
    );
  }

  endSession(userId: string): Observable<boolean> {
    if (!userId) {
      return of(false);
    }

    return this.postChat<any>('chat/end-session', {userId}).pipe(
      map((result: any) => Boolean(this.unwrapData<boolean>(result))),
      catchError(() => of(false))
    );
  }

  resetState(): void {
    this.disconnectRealtime();
    this.threadsState.set([]);
    this.messagesState.set({});
    this.typingState.set({});
    this.threadsLoadInFlight = false;
    this.threadsLoadedUserId = '';
  }

  appendMessage(message: ChatMessage): void {
    this.logDebug('[Chat][SignalR] appendMessage', this.getRealtimeLogState({
      messageId: message.id,
      threadId: message.threadId
    }));
    const messagesByThread = {...this.messagesState()};
    const existingMessages = messagesByThread[message.threadId] || [];
    const messageExists = existingMessages.some((x) => x.id === message.id);
    const list = messageExists ? existingMessages : [...existingMessages, message];
    messagesByThread[message.threadId] = list;
    this.messagesState.set(messagesByThread);

    const isActiveThread = message.threadId === this.activeThreadId;
    const isOwnMessage = message.senderUserId === this.realtimeUserId;

    const threads = this.threadsState().map((thread) => {
      if (thread.id !== message.threadId) {
        return thread;
      }

      const unreadIncrement = (!isActiveThread && !isOwnMessage) ? 1 : 0;
      return {
        ...thread,
        lastMessage: message,
        unreadCount: (thread.unreadCount || 0) + unreadIncrement
      };
    });

    this.threadsState.set(threads);
    this.unreadCountState.set(threads.reduce((sum, thread) => sum + (thread.unreadCount || 0), 0));
  }

  upsertThread(thread: ChatThread): void {
    this.logDebug('[Chat][SignalR] upsertThread', this.getRealtimeLogState({
      threadId: thread.id
    }));
    const normalizedThread = this.normalizeThread(thread);
    const threads = [...this.threadsState()];
    const index = threads.findIndex((x) => x.id === normalizedThread.id);

    if (index >= 0) {
      threads[index] = {
        ...threads[index],
        ...normalizedThread
      };
    } else {
      threads.unshift(normalizedThread);
    }

    this.threadsState.set(threads);
  }

  private normalizeThread(thread: any): ChatThread {
    const participants = Array.isArray(thread?.participants)
      ? thread.participants
      : (Array.isArray(thread?.Participants) ? thread.Participants : []);

    const lastMessage = this.extractThreadLastMessage(thread);

    return {
      id: (thread?.id ?? thread?.Id ?? '').toString(),
      title: (thread?.title ?? thread?.Title ?? '').toString(),
      participants: participants.map((participant: any) => ({
        userId: (participant?.userId ?? participant?.UserId ?? '').toString(),
        name: (participant?.userName ?? participant?.UserName ?? participant?.name ?? participant?.Name ?? '').toString(),
        avatarUrl: this.resolveAvatarUrl(participant?.avatarUrl ?? participant?.AvatarUrl)
      })),
      lastMessage: lastMessage ? this.normalizeMessage(lastMessage) : undefined,
      unreadCount: Number(thread?.unreadCount ?? thread?.UnreadCount ?? 0)
    };
  }

  private extractThreadLastMessage(thread: any): any {
    const directLastMessage = thread?.lastMessage
      ?? thread?.LastMessage
      ?? thread?.latestMessage
      ?? thread?.LatestMessage
      ?? thread?.recentMessage
      ?? thread?.RecentMessage;

    if (directLastMessage) {
      return directLastMessage;
    }

    const messages = Array.isArray(thread?.messages)
      ? thread.messages
      : (Array.isArray(thread?.Messages) ? thread.Messages : []);

    if (messages.length === 0) {
      return null;
    }

    return messages[messages.length - 1];
  }

  private isThreadDeleted(thread: any): boolean {
    return Boolean(
      thread?.isDeleted
      || thread?.IsDeleted
      || thread?.deleted
      || thread?.Deleted
      || thread?.deletedAt
      || thread?.DeletedAt
    );
  }

  // The REST "send message"/"upload attachment" endpoints only return the bare created
  // message record (no joined sender avatar), unlike the thread/message list endpoints
  // which include it. Without this, a user's own freshly-sent message would show a
  // missing/default avatar until the thread/messages were reloaded. Since we always know
  // our own avatar locally (same source the topbar uses), fill it in for our own messages
  // when the backend didn't supply one.
  private getOwnAvatarUrl(): string {
    try {
      const preferencesRaw = this.storageService.StorageGet('Preferences');
      const preferences = typeof preferencesRaw === 'string' ? JSON.parse(preferencesRaw) : preferencesRaw;
      const avatarRelativePath = preferences?.avatarUrl;
      // Mirror header.component's handling of the backend's "default-avatar.png" placeholder
      // (a path that doesn't actually exist on the avatars storage server) - treat it the
      // same as "no avatar" rather than resolving/requesting a 404.
      if (!avatarRelativePath || /default-avatar\.(png|jpg|jpeg|gif|svg)$/i.test(avatarRelativePath)) {
        return '';
      }
      return this.resolveAvatarUrl(avatarRelativePath);
    } catch {
      return '';
    }
  }

  private normalizeMessage(message: any): ChatMessage {
    const senderUserId = (message?.senderUserId ?? message?.SenderUserId ?? '').toString();
    let senderAvatarUrl = this.resolveAvatarUrl(message?.senderAvatarUrl ?? message?.SenderAvatarUrl);
    if (!senderAvatarUrl && senderUserId && senderUserId === this.realtimeUserId) {
      senderAvatarUrl = this.getOwnAvatarUrl();
    }

    return {
      id: (message?.id ?? message?.Id ?? '').toString(),
      threadId: (message?.threadId ?? message?.ThreadId ?? '').toString(),
      senderUserId,
      senderName: (message?.senderName ?? message?.SenderName ?? message?.senderUserName ?? message?.SenderUserName ?? '').toString(),
      senderAvatarUrl,
      body: (message?.body ?? message?.Body ?? '').toString(),
      attachmentUrl: message?.attachmentUrl ?? message?.AttachmentUrl,
      attachmentFileName: message?.attachmentFileName ?? message?.AttachmentFileName,
      attachmentContentType: message?.attachmentContentType ?? message?.AttachmentContentType,
      attachmentSize: Number(message?.attachmentSize ?? message?.AttachmentSize ?? 0) || undefined,
      createdAt: (message?.createdAt ?? message?.CreatedAt ?? '').toString(),
      readByUserIds: Array.isArray(message?.readByUserIds)
        ? message.readByUserIds.map((id: unknown) => id?.toString?.() ?? '')
        : (Array.isArray(message?.ReadByUserIds)
          ? message.ReadByUserIds.map((id: unknown) => id?.toString?.() ?? '')
          : [])
    };
  }

  markThreadAsRead(threadId: string, userId: string): void {
    this.postChat<any>('chat/read', {threadId, userId}).subscribe(() => {
      const updatedThreads = this.threadsState().map((thread) =>
        thread.id === threadId ? {...thread, unreadCount: 0} : thread
      );
      this.threadsState.set(updatedThreads);
      this.unreadCountState.set(updatedThreads.reduce((sum, thread) => sum + (thread.unreadCount || 0), 0));

      const messagesByThread = {...this.messagesState()};
      const targetMessages = messagesByThread[threadId] || [];
      messagesByThread[threadId] = targetMessages.map((message) => {
        if (message.readByUserIds.includes(userId)) {
          return message;
        }

        return {
          ...message,
          readByUserIds: [...message.readByUserIds, userId]
        };
      });
      this.messagesState.set(messagesByThread);
    });
  }

  markAllAsRead(userId: string): void {
    this.postChat<any>('chat/read/all', {userId}).subscribe(() => {
      const updatedThreads = this.threadsState().map((thread) => ({...thread, unreadCount: 0}));
      this.threadsState.set(updatedThreads);
      this.unreadCountState.set(0);
    });
  }

  refreshUnreadCount(userId: string): Observable<number> {
    const normalizedUserId = (userId || '').trim();
    if (!normalizedUserId || !this.canSendChatHttpRequests()) {
      this.unreadCountState.set(0);
      return of(0);
    }

    return this.getChat<any>(`chat/unread-count?userId=${encodeURIComponent(normalizedUserId)}`).pipe(
      map((result) => this.extractUnreadCount(result)),
      map((count) => Math.max(0, Number.isFinite(count) ? count : 0)),
      tap((count) => this.unreadCountState.set(count)),
      catchError(() => {
        this.unreadCountState.set(0);
        return of(0);
      }),
      take(1)
    );
  }

  private extractUnreadCount(result: any): number {
    // console.log('count', result);
    const payload = result?.Data ?? result?.data ?? result;

    if (Array.isArray(payload)) {
      const first = payload[0];
      if (typeof first === 'number') {
        return first;
      }

      if (first && typeof first === 'object') {
        const firstValue = first.count
          ?? first.Count
          ?? first.unreadCount
          ?? first.UnreadCount;
        return Number(firstValue ?? 0);
      }

      return Number(payload[0] ?? 0);
    }

    if (typeof payload === 'number') {
      return payload;
    }

    if (payload && typeof payload === 'object') {
      const objectValue = payload.count
        ?? payload.Count
        ?? payload.unreadCount
        ?? payload.UnreadCount;
      return Number(objectValue ?? 0);
    }

    return Number(payload ?? 0);
  }

  setTyping(threadId: string, userId: string, displayName: string, isTyping: boolean): void {
    if (!threadId || !userId) {
      return;
    }

    const currentUserRaw = localStorage.getItem('currentUser');
    if (!currentUserRaw) {
      return;
    }

    this.postChat<any>('chat/typing', {threadId, userId, displayName, isTyping}).subscribe();
  }

  private updateTypingState(state: ChatTypingState): void {
    this.logDebug('[Chat][SignalR] updateTypingState', this.getRealtimeLogState({state}));
    const current = {...this.typingState()};
    const list = [...(current[state.threadId] || [])].filter((x) => x.userId !== state.userId);

    if (state.isTyping) {
      list.push(state);
    }

    current[state.threadId] = list;
    this.typingState.set(current);
  }

  getTopbarPreviewItems$(limit = 8): Observable<ChatPreviewItem[]> {
    return toObservable(this.topbarPreviewItemsState, {injector: this.injector}).pipe(
      map((items) => items.slice(0, limit)),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    );
  }

  getUnreadCount$(): Observable<number> {
    return toObservable(this.unreadCountState, {injector: this.injector}).pipe(
      distinctUntilChanged()
    );
  }
}
